/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import React, { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { envType } from '@atlassiansox/analytics-web-client';
import { toEnvironment } from '@jira/platform__tenant-context-types/src/tenant-context.tsx';
import { choreographerFactory } from '@atlassian/ipm-choreographer/factory';
import { MessageDeliveryStatus, ProductIds } from '@atlassian/ipm-choreographer/constants';
import { PendoChoreographerPlugin } from '@atlassian/ipm-choreographer/pendo-choreographer-plugin';
import { Choreographer } from '@atlassian/ipm-choreographer/choreographer';
import type {
	MaybeChoreographedComponentProps,
	ShouldBeChoreographedFunction,
	UseChoreographerFunction,
} from '@atlassian/ipm-choreographer/factory/types'; // index.d.ts
import { fg } from '@atlassian/jira-feature-gating';
import getMeta from '@jira/platform__utils-get-meta/src/utils/get-meta/index.tsx';
import {
	ChoreographerContextProvider,
	useChoreographedStatusContext,
} from './ChoreographerContextProvider.tsx';

import type {
	ChoreographedComponentProps,
	ChoreographedMessage,
	MessageDeliveryStatuses,
} from './types.tsx';

// oxlint-disable-next-line typescript/consistent-type-assertions
const environment = (toEnvironment(getMeta('ajs-environment')) ?? 'prod') as envType;

type ChoreographerFactory = ReturnType<typeof choreographerFactory>;

const createChoreographerFactory = (): Pick<
	ChoreographerFactory,
	'useChoreographer' | 'withChoreographedToggle' | 'shouldBeChoreographed'
> => {
	if (__SERVER__) {
		return {
			useChoreographer: (..._: Parameters<ChoreographerFactory['useChoreographer']>) => ({
				startMessage: () => Promise.resolve(MessageDeliveryStatus.STARTED),
				stopMessage: () => Promise.resolve(MessageDeliveryStatus.STOPPED),
				// oxlint-disable-next-line typescript/consistent-type-assertions
				toggleMessage: () => Promise.resolve('' as MessageDeliveryStatuses),
			}),
			withChoreographedToggle: <T extends { [K in keyof T]: T[K] }>(
				Component: React.ComponentType<T>,
				// oxlint-disable-next-line typescript/consistent-type-assertions
			) => Component as (props: T & ChoreographedComponentProps) => JSX.Element | null,
			shouldBeChoreographed: (
				_props?: MaybeChoreographedComponentProps,
			): _props is ChoreographedMessage => false,
		};
	}

	return choreographerFactory(ProductIds.JIRA, environment);
};

let pendoPlugin: PendoChoreographerPlugin | undefined;
export function getPendoPlugin(env: envType): PendoChoreographerPlugin {
	if (!pendoPlugin) {
		pendoPlugin = new PendoChoreographerPlugin(env, { sourceProductId: ProductIds.JIRA });
	}
	return pendoPlugin;
}

/**
 * Stupid hack alert: useChoreographer is throwing a TS compiler error about using MessageEventType
 * internally, and not being able to resolve that name... The usual fix for this is to add the
 * offending type to the project's exports, and then import it here so that the compiler has some
 * knowledge of what that type definition is... In this case, it doesn't seem to be working, but
 * for whatever reason, I can cast the choreographerFactory's return type using nothing more than
 * the information provided by the ReturnType<typeof choreographerFactory> type above, and then
 * define the useChoreographer type as an args list that conforms to its Parameters list, and a
 * return type that conforms to its ReturnType, and that seems to work just fine. Hopefully, somebody
 * can figure out what's going wrong here, and remove this idiocy in favor of something more sane.
 */
const dest = createChoreographerFactory();
const useChoreographer: UseChoreographerFunction = dest.useChoreographer;
const withChoreographedToggleFromFactory = dest.withChoreographedToggle;
const shouldBeChoreographed: ShouldBeChoreographedFunction = dest.shouldBeChoreographed;

const withChoreographedToggle = <T,>(
	Component: React.ComponentType<T>,
	toggleableProp: keyof T,
	toggleableContentProp?: keyof T,
): {
	(props: T & ChoreographedComponentProps): React.JSX.Element;
	displayName: string;
} => {
	const WithChoreographedToggleComponent = (props: T & ChoreographedComponentProps) => {
		const { isAlreadyChoreographedAtAncestor } = useChoreographedStatusContext();
		const ChoreographedComponent = useMemo(
			() => withChoreographedToggleFromFactory(Component, toggleableProp),
			[],
		);

		if (isAlreadyChoreographedAtAncestor || !fg('jira_with_choreographed_toggle_fix')) {
			return <Component {...props} />;
		}

		const propsWithContextProviderWrapper = {
			...props,
		};

		if (toggleableContentProp) {
			const { [toggleableContentProp]: toggleableContentPropValue } =
				propsWithContextProviderWrapper;
			if (typeof toggleableContentPropValue === 'function') {
				// @ts-expect-error TS doesn't know this is a callback with variable params that returns ReactNode, but we do
				propsWithContextProviderWrapper[toggleableContentProp] = (...args: unknown[]) => (
					<ChoreographerContextProvider isChoreographed>
						{toggleableContentPropValue(...args)}
					</ChoreographerContextProvider>
				);
			} else {
				// oxlint-disable-next-line typescript/consistent-type-assertions
				const content = toggleableContentPropValue as ReactNode;

				// @ts-expect-error TS doesn't know this is a ReactNode, but we do
				propsWithContextProviderWrapper[toggleableContentProp] = (
					<ChoreographerContextProvider isChoreographed>{content}</ChoreographerContextProvider>
				);
			}
		}

		return <ChoreographedComponent {...propsWithContextProviderWrapper} />;
	};

	WithChoreographedToggleComponent.displayName = `WithChoreographedToggle(${Component.displayName ?? Component.name ?? 'Component'})`;

	return WithChoreographedToggleComponent;
};

const useChoreographedRender = ({
	messageId,
	onMessageDisposition,
}: ChoreographedComponentProps): boolean => {
	const { isAlreadyChoreographedAtAncestor } = useChoreographedStatusContext();
	const [shouldRender, setShouldRender] = useState(false);
	const { startMessage, stopMessage } = useChoreographer(messageId, {
		start: () => setShouldRender(true),
	});

	useEffect(() => {
		if (!isAlreadyChoreographedAtAncestor) {
			const startMessageAsync = async () => {
				const disposition = await startMessage();
				// eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent
				onMessageDisposition?.(disposition);
			};

			startMessageAsync();

			return () => {
				stopMessage();
			};
		}
	}, [onMessageDisposition, startMessage, stopMessage, isAlreadyChoreographedAtAncestor]);

	return shouldRender || isAlreadyChoreographedAtAncestor;
};

/**
 * Disable in-product messages orchestrated with Choreographer while rendering in an iframe. This **does not** disable
 * messages that are not controlled via Choreographer plugins, e.g. Engagement Platform message with
 * `enableChoreographer: false`.
 *
 * If rendered outside an iframe then this hook is a noop.
 */
export const useDisableInAppMessagesForIframe = (): void => {
	const cleanup = useRef<(() => void) | undefined>();
	const disabled = useRef(false);
	if (!disabled.current) {
		cleanup.current = Choreographer.getInstance().disableChoreographerForIframe();
		disabled.current = true;
	}
	useEffect(() => () => cleanup.current?.(), []);
};

export { useChoreographer, useChoreographedRender, withChoreographedToggle, shouldBeChoreographed };
